<!-- 租户管理平台首页 — 原型风格 -->
<template>
  <div class="home-dashboard">
    <!-- ========== ① 欢迎 + 系统公告 ========== -->
    <div class="welcome-bar">
      <div class="welcome-bar__left">
        <img class="welcome-avatar" :src="displayAvatar" alt="" @error="handleAvatarError" />
        <h2 class="welcome-text">您好，{{ username }}祝您开心每一天！</h2>
      </div>
      <div class="welcome-bar__notice">
        <span class="notice-speaker">
          <Icon icon="ep:microphone" :size="14" />
        </span>
        <span class="notice-ticker__label">系统公告：</span>
        <div class="notice-ticker__viewport">
          <Transition name="notice-slide" mode="out-in">
            <span v-if="currentNotice" :key="noticeIndex" class="notice-ticker__text">
              {{ currentNotice.title }}
            </span>
          </Transition>
        </div>
        <span class="notice-ticker__date">{{
          currentNotice ? formatTime(currentNotice.date, 'yyyy-MM-dd') : ''
        }}</span>
      </div>
    </div>

    <!-- ========== ② 轮播 + 快捷入口 ========== -->
    <div class="hero-row">
      <div class="hero-carousel">
        <div class="carousel-slides">
          <div
            v-for="(slide, idx) in slides"
            :key="idx"
            class="carousel-slide"
            :class="{ active: currentSlide === idx }"
            :style="{ backgroundColor: slide.background }"
          >
            <img class="slide-image" :src="slide.image" :alt="slide.alt" />
          </div>
        </div>
        <button class="carousel-arrow arrow-prev" @click="changeSlide(-1)">
          <Icon icon="ep:arrow-left" :size="16" />
        </button>
        <button class="carousel-arrow arrow-next" @click="changeSlide(1)">
          <Icon icon="ep:arrow-right" :size="16" />
        </button>
        <div class="carousel-dots">
          <div
            v-for="(_, idx) in slides"
            :key="idx"
            class="dot"
            :class="{ active: currentSlide === idx }"
            @click="currentSlide = idx"
          />
        </div>
      </div>

      <div class="quick-panel">
        <div class="quick-panel__head">
          <span class="section-title">快捷入口</span>
          <button type="button" class="quick-panel__more" @click="goMoreApps">更多应用 →</button>
        </div>
        <div v-if="shortcut.length" class="quick-grid">
          <div
            v-for="item in shortcut"
            :key="item.name"
            class="quick-item"
            @click="handleShortcutClick(item)"
          >
            <div
              class="quick-icon"
              :style="{
                background: item.iconUrl ? 'transparent' : hexToRgba(item.color, 0.12),
                color: item.color
              }"
            >
              <ModuleCardIcon :icon-url="item.iconUrl" :icon="item.icon" :size="22" />
            </div>
            <span class="quick-label">{{ item.name }}</span>
          </div>
        </div>
        <div v-else class="quick-empty">暂无已开通应用</div>
      </div>
    </div>

    <!-- ========== ③ 数据分析 ========== -->
    <div class="section-header">
      <span class="section-title">数据分析</span>
    </div>
    <div class="charts-row">
      <div class="chart-card">
        <div class="chart-title">用户浏览来源分析</div>
        <div class="chart-container">
          <Echart :options="pieOptionsData" :height="260" />
        </div>
      </div>
      <div class="chart-card">
        <div class="chart-title">周用户活跃趋势</div>
        <div class="chart-container">
          <Echart :options="barOptionsData" :height="260" />
        </div>
      </div>
    </div>

    <!-- ========== ⑤ 核心业务模块 ========== -->
    <div class="section-header">
      <span class="section-title">核心业务模块</span>
      <span class="view-all" @click="goMoreApps">查看更多</span>
    </div>
    <div v-if="projects.length" class="module-grid">
      <div
        v-for="item in projects"
        :key="item.name"
        class="module-card"
        @click="handleProjectClick(item.name)"
      >
        <div class="module-top">
          <div
            class="module-icon"
            :style="{
              background: item.iconUrl ? 'transparent' : hexToRgba(item.color, 0.1),
              color: item.color
            }"
          >
            <ModuleCardIcon :icon-url="item.iconUrl" :icon="item.icon" :size="22" />
          </div>
          <div>
            <div class="module-name">{{ item.name }}</div>
            <div class="module-desc">{{ item.personal }}</div>
          </div>
        </div>
        <div class="module-info">{{ item.message }}</div>
        <div class="module-date">{{ formatTime(item.time, 'yyyy-MM-dd') }}</div>
      </div>
    </div>
    <div v-else class="module-empty">暂无核心业务模块，请在模块管理中开启推荐</div>
  </div>
</template>

<script lang="ts" setup>
import { set } from 'lodash-es'
import { EChartsOption } from 'echarts'
import { formatTime } from '@/utils'
import { useUserStore } from '@/store/modules/user'
import { usePermissionStore } from '@/store/modules/permission'
import defaultAvatar from '@/assets/imgs/avatar.jpg'
import type { Project, Notice, Shortcut } from './types'
import { pieOptions, barOptions } from './echarts-data'
import { onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import * as ModuleApi from '@/api/system/module'
import {
  buildModuleCards,
  getModuleCardDisplay,
  isHomeCoreModuleEnabled,
  HOME_CORE_MODULE_LIMIT,
  type ModuleCard
} from '@/utils/moduleCard'
import {
  AI_APPLICATION_CENTER_PREFIX,
  launchStandaloneAppAsync,
  resolveStandaloneAppKey
} from '@/utils/standaloneApp'
import { AI_APP_CENTER_MODULES } from '@/constants/aiAppCenter'
import { BannerApi } from '@/api/system/banner'
import { ModuleCardIcon } from '@/components/ModuleCardIcon'
import { DEFAULT_WORKBENCH_BANNERS, type WorkbenchSlide } from './workbenchBanners'

defineOptions({ name: 'Index' })

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const permissionStore = usePermissionStore()
const loading = ref(true)
const avatar = computed(() => String(userStore.user?.avatar || '').trim())
const avatarLoadError = ref(false)
const displayAvatar = computed(() =>
  avatar.value && !avatarLoadError.value ? avatar.value : defaultAvatar
)
const username = computed(() => userStore.user?.nickname ?? 'Admin')
const pieOptionsData = reactive<EChartsOption>(pieOptions) as EChartsOption

watch(avatar, () => {
  avatarLoadError.value = false
})
const handleAvatarError = () => {
  avatarLoadError.value = true
}

// 写死统计占位，暂隐藏
// let totalSate = reactive<WorkplaceTotal>({ module: 0, access: 0, todo: 0 })
// const getCount = async () => {
//   const data = { module: 20, access: 2340, todo: 10 }
//   totalSate = Object.assign(totalSate, data)
// }

// 轮播图：有租户配置用配置，否则默认 5 张
const slides = ref<WorkbenchSlide[]>([...DEFAULT_WORKBENCH_BANNERS])
const currentSlide = ref(0)
function changeSlide(delta: number) {
  const len = slides.value.length || 1
  currentSlide.value = (currentSlide.value + delta + len) % len
}
const loadBanners = async () => {
  try {
    const data = await BannerApi.getPublicList({ pageNo: 1, pageSize: 20 })
    const list = (data?.list || [])
      .filter((item) => item?.imageUrl && String(item.imageUrl).trim())
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    if (list.length) {
      slides.value = list.map((item) => ({
        image: String(item.imageUrl).trim(),
        alt: item.title || '工作台轮播',
        background: '#f5f8fc'
      }))
      currentSlide.value = 0
      return
    }
  } catch {
    /* 接口失败回落默认 */
  }
  slides.value = [...DEFAULT_WORKBENCH_BANNERS]
  currentSlide.value = 0
}

// ========== 动态模块加载（与 AI应用/中台 Tab 同步） ==========

// 合并所有中心的模块卡片（ai-app + business）
const allModuleCards = ref<ModuleCard[]>([])
const availableModules = ref<ModuleApi.ModuleVO[]>([])

/** 与 AI 应用中心 / 产品中心一致：超管看全量已启用模块，普通租户看已开通未过期 */
const isSuperAdmin = () => userStore.getRoles.includes('super_admin')

/** 首页在 setup 末尾即拉数，须等 permission 路由就绪，否则 buildModuleCards 鉴权为空 */
const ensurePermissionRoutes = async () => {
  if (!userStore.getIsSetUser) {
    await userStore.setUserInfoAction()
  }
  if (permissionStore.getRouters.length === 0) {
    await permissionStore.generateRoutes()
  }
}

const loadModules = async () => {
  try {
    await ensurePermissionRoutes()
    const superAdmin = isSuperAdmin()
    const modules = superAdmin
      ? await ModuleApi.getEnabledModuleList()
      : await ModuleApi.getAvailableModuleList()
    availableModules.value = modules
    const menuRouters = permissionStore.getMenuRouters
    const allRouters = permissionStore.getRouters
    const aiAppCards = buildModuleCards(modules, 'ai-app', menuRouters, allRouters, superAdmin)
    const businessCards = buildModuleCards(modules, 'business', menuRouters, allRouters, superAdmin)
    // 按 key 去重合并
    const merged = new Map<string, ModuleCard>()
    for (const card of [...aiAppCards, ...businessCards]) {
      merged.set(card.key, card)
    }
    allModuleCards.value = Array.from(merged.values())
  } catch (error) {
    console.warn('[Home] loadModules failed', error)
    availableModules.value = []
    allModuleCards.value = []
  }
}

/** 快捷入口固定 8 个（与原型一致），不走推荐开关 */
const HOME_SHORTCUT_KEYS = [
  'aiSecretary',
  'promotion',
  'digitalCard',
  'aiModel',
  'mall',
  'lawyer',
  'lawyer-firm',
  'business'
] as const
const HOME_SHORTCUT_LABEL_OVERRIDE: Record<string, string> = {
  'lawyer-firm': '律师 ERP'
}
/** 快捷入口展示图标（对齐原型色块语义，可点性仍走注册表路径） */
const HOME_SHORTCUT_ICON_OVERRIDE: Record<string, string> = {
  aiSecretary: 'ep:user',
  promotion: 'ep:present',
  digitalCard: 'ep:postcard',
  aiModel: 'ep:cpu',
  mall: 'ep:shop',
  lawyer: 'ep:reading',
  'lawyer-firm': 'ep:briefcase',
  business: 'ep:location'
}
let shortcut = reactive<Shortcut[]>([])
const resolveShortcutUrl = (key: string, card?: ModuleCard) => {
  const reg = AI_APP_CENTER_MODULES.find((item) => item.key === key)
  const candidates = [
    card?.standalonePath,
    reg?.standalonePath,
    card?.path,
    reg?.homePath,
    reg?.path
  ]
  for (const raw of candidates) {
    const value = String(raw || '').trim()
    if (!value || value === '/' || value === 'about:blank') continue
    if (value.startsWith('http://') || value.startsWith('https://')) return value
    if (
      value.startsWith(AI_APPLICATION_CENTER_PREFIX) ||
      value.startsWith('/ai-business-center')
    ) {
      return value
    }
    return `${AI_APPLICATION_CENTER_PREFIX}${value.startsWith('/') ? value : `/${value}`}`
  }
  return `${AI_APPLICATION_CENTER_PREFIX}/${key}`
}
/** 快捷入口：固定展示原型 8 个应用，优先用已开通卡片，否则用注册表兜底保证可点 */
const buildShortcuts = () => {
  const data: Shortcut[] = HOME_SHORTCUT_KEYS.map((key) => {
    const card = allModuleCards.value.find((item) => item.key === key)
    const reg = AI_APP_CENTER_MODULES.find((item) => item.key === key)
    return {
      key,
      name: HOME_SHORTCUT_LABEL_OVERRIDE[key] || card?.label || reg?.label || key,
      icon: HOME_SHORTCUT_ICON_OVERRIDE[key] || card?.icon || reg?.icon || 'ep:grid',
      iconUrl: card?.iconUrl,
      url: resolveShortcutUrl(key, card),
      color: card?.color || reg?.color || '#1677ff'
    }
  })
  shortcut.length = 0
  shortcut.push(...data)
}
const goMoreApps = () => {
  router.push('/ai-Application-center')
}

// 核心业务模块：模块管理中开启推荐的项，最多 12，每排 4
let projects = reactive<Project[]>([])
const buildProjects = () => {
  const flagged = [...availableModules.value]
    .filter((mod) => isHomeCoreModuleEnabled(mod))
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .slice(0, HOME_CORE_MODULE_LIMIT)
  const data: Project[] = flagged.map((mod) => {
    const key = resolveStandaloneAppKey(mod.name)
    const card = allModuleCards.value.find((item) => item.key === key || item.label === mod.title)
    const display = getModuleCardDisplay(mod)
    return {
      name: display.label,
      icon: display.icon,
      iconUrl: display.iconUrl || card?.iconUrl,
      message: card?.desc || mod.description || '',
      personal: '',
      time: new Date(),
      color: display.color
    }
  })
  projects.length = 0
  projects.push(...data)
}

// 通知公告（演示数据）— 欢迎行右侧逐条滚动
const notice = reactive<Notice[]>([])
const noticeIndex = ref(0)
const currentNotice = computed(() => notice[noticeIndex.value] || null)
let noticeTimer: ReturnType<typeof setInterval> | null = null
const startNoticeTimer = () => {
  if (noticeTimer) clearInterval(noticeTimer)
  if (notice.length <= 1) return
  noticeTimer = setInterval(() => {
    noticeIndex.value = (noticeIndex.value + 1) % notice.length
  }, 3500)
}
const getNotice = async () => {
  const data: Notice[] = [
    {
      title: '系统支持 JDK 8/17/21，Vue 2/3',
      type: '技术兼容性',
      keys: ['JDK', 'Vue'],
      date: new Date()
    },
    {
      title: '后端提供 Spring Boot 2.7/3.2 + Cloud 双架构',
      type: '架构灵活性',
      keys: ['Boot', 'Cloud'],
      date: new Date()
    },
    {
      title: '国内使用最广泛的快速开发平台，远超 10w+ 企业使用',
      type: '广泛企业认可',
      keys: ['最广泛', '10w+'],
      date: new Date()
    }
  ]
  notice.splice(0, notice.length, ...data)
  noticeIndex.value = 0
  startNoticeTimer()
}

// 模块卡片点击跳转
const handleModuleCardClick = async (card: ModuleCard) => {
  if (card.openMode === 'same-window') {
    router.push(card.path)
    return
  }
  const target = card.standalonePath || card.path
  // plain：不带独立壳参数直接新窗口打开（AI智慧餐饮统一管理入口）
  if (card.openMode === 'plain') {
    const popup = window.open(target, '_blank')
    if (!popup) {
      ElMessage.error('浏览器阻止了新窗口，请允许本站打开弹窗后重试')
    }
    return
  }
  try {
    const opened = await launchStandaloneAppAsync(target, card.key)
    if (!opened) {
      ElMessage.error('浏览器阻止了新窗口，请允许本站打开弹窗后重试')
    }
  } catch {
    ElMessage.error('打开独立窗失败，请稍后重试')
  }
}

// 图表数据（标签始终显示中文）
const getUserAccessSource = async () => {
  const data = [
    { value: 335, name: '直接访问' },
    { value: 310, name: '邮件营销' },
    { value: 234, name: '联盟广告' },
    { value: 135, name: '视频广告' },
    { value: 1548, name: '搜索引擎' }
  ]
  set(
    pieOptionsData,
    'legend.data',
    data.map((v) => v.name)
  )
  pieOptionsData!.series![0].data = data
}

const barOptionsData = reactive<EChartsOption>(barOptions) as EChartsOption
const getWeeklyUserActivity = async () => {
  const data = [
    { value: 13253, name: '周一' },
    { value: 34235, name: '周二' },
    { value: 26321, name: '周三' },
    { value: 12340, name: '周四' },
    { value: 24643, name: '周五' },
    { value: 1322, name: '周六' },
    { value: 1324, name: '周日' }
  ]
  set(
    barOptionsData,
    'xAxis.data',
    data.map((v) => v.name)
  )
  set(barOptionsData, 'series', [{ name: '活跃量', data: data.map((v) => v.value), type: 'bar' }])
}

const getAllApi = async () => {
  await Promise.all([
    // getCount(),
    loadModules(),
    loadBanners(),
    getNotice(),
    getUserAccessSource(),
    getWeeklyUserActivity()
  ])
  // 模块加载完成后构建快捷入口和核心业务模块
  buildShortcuts()
  buildProjects()
  loading.value = false
}

// 轮播自动播放
let slideTimer: ReturnType<typeof setInterval> | null = null
const startSlideTimer = () => {
  if (slideTimer) clearInterval(slideTimer)
  slideTimer = setInterval(() => {
    const len = slides.value.length || 1
    currentSlide.value = (currentSlide.value + 1) % len
  }, 5000)
}
onMounted(() => {
  startSlideTimer()
  void getAllApi()
})
onBeforeUnmount(() => {
  if (slideTimer) clearInterval(slideTimer)
  if (noticeTimer) clearInterval(noticeTimer)
})

// 工具函数
function hexToRgba(hex: string, alpha: number) {
  if (!hex) return `rgba(108,92,231,${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

const handleProjectClick = (name: string) => {
  const card = allModuleCards.value.find((c) => c.label === name)
  if (card) {
    handleModuleCardClick(card)
  }
}
const handleShortcutClick = async (item: Shortcut) => {
  const card = allModuleCards.value.find(
    (c) =>
      c.key === item.key ||
      c.label === item.name ||
      (c.standalonePath || c.path) === item.url
  )
  if (card) {
    await handleModuleCardClick(card)
    return
  }
  // 租户未开通时仍按注册表独立窗打开，避免点了没反应
  try {
    const opened = await launchStandaloneAppAsync(item.url, item.key)
    if (!opened) {
      ElMessage.error('浏览器阻止了新窗口，请允许本站打开弹窗后重试')
    }
  } catch {
    ElMessage.error('打开应用失败，请稍后重试')
  }
}
</script>

<style lang="scss" scoped>
$accent: #0089ff; // 钉钉蓝
$accent-soft: #e8f3ff;
$card-bg: var(--app-card-bg-color);
$text-primary: var(--app-content-text-color);
$text-secondary: var(--app-content-secondary-text-color);
$text-muted: var(--app-content-muted-text-color);
$border-color: var(--app-card-border-color);
$shadow-sm: 0 1px 2px rgba(23, 26, 29, 0.04);
$shadow-md: 0 4px 12px rgba(23, 26, 29, 0.06);

.home-dashboard {
  // 白底内容区：卡片靠细边框分层，少阴影
  padding-top: 2px;

  .welcome-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 16px;
    padding: 0 20px;
    height: 56px;
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 16px;
    overflow: hidden;
    @media (max-width: 900px) {
      flex-direction: column;
      align-items: stretch;
      height: auto;
      gap: 8px;
      padding: 12px 16px;
    }
  }
  .welcome-bar__left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 0 1 auto;
  }
  .welcome-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background: #e8f3ff;
  }
  .welcome-text {
    margin: 0;
    color: $text-primary;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  // 公告贴右、随内容收窄，不再铺整行灰底
  .welcome-bar__notice {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    flex: 0 1 420px;
    max-width: 420px;
    min-width: 0;
    height: 32px;
    padding: 0;
    background: transparent;
    @media (max-width: 900px) {
      flex: none;
      max-width: none;
      justify-content: flex-start;
    }
  }
  .notice-speaker {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: $accent-soft;
    color: $accent;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .notice-ticker__label {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    color: $accent;
    white-space: nowrap;
  }
  .notice-ticker__viewport {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 240px;
    height: 22px;
    overflow: hidden;
  }
  .notice-ticker__text {
    position: absolute;
    inset: 0;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    line-height: 22px;
    color: $text-secondary;
  }
  .notice-ticker__date {
    flex: 0 0 auto;
    font-size: 12px;
    color: $text-muted;
    white-space: nowrap;
  }

  .hero-row {
    display: grid;
    grid-template-columns: 1.45fr 1fr;
    gap: 16px;
    margin-bottom: 22px;
    align-items: stretch;
    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }
  }

  .notice-slide-enter-active,
  .notice-slide-leave-active {
    transition:
      transform 0.35s ease,
      opacity 0.35s ease;
  }
  .notice-slide-enter-from {
    transform: translateY(18px);
    opacity: 0;
  }
  .notice-slide-leave-to {
    transform: translateY(-18px);
    opacity: 0;
  }

  .hero-carousel {
    position: relative;
    height: 320px;
    min-height: 320px;
    border-radius: 16px;
    overflow: hidden;
    background: $card-bg;
    border: none;
  }
  .carousel-slides {
    position: relative;
    height: 100%;
  }
  .carousel-slide {
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 0.5s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    &.active {
      opacity: 1;
    }
  }
  .slide-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: fill;
  }
  .carousel-dots {
    position: absolute;
    bottom: 12px;
    left: 36px;
    display: flex;
    gap: 6px;
  }
  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(0, 137, 255, 0.28);
    cursor: pointer;
    transition: all 0.25s;
    &.active {
      width: 20px;
      border-radius: 4px;
      background: $accent;
    }
  }
  .carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--home-carousel-arrow-bg);
    border: 1px solid var(--home-carousel-arrow-border);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: $text-secondary;
    font-size: 15px;
    transition: all 0.15s;
    z-index: 2;
    &:hover {
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    }
  }
  .arrow-prev {
    left: 10px;
  }
  .arrow-next {
    right: 10px;
  }

  .quick-panel {
    display: flex;
    flex-direction: column;
    min-height: 320px;
    padding: 16px 18px 14px;
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 16px;
  }
  .quick-panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
  }
  .quick-panel__more {
    border: none;
    background: transparent;
    padding: 0;
    font-size: 13px;
    color: $accent;
    cursor: pointer;
    font-weight: 400;
    &:hover {
      text-decoration: underline;
    }
  }
  .quick-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    flex: 1;
  }
  .quick-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: $text-muted;
  }
  .quick-item {
    background: #f7f8fa;
    border: 1px solid transparent;
    border-radius: 12px;
    padding: 14px 6px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition: background-color 0.18s ease;
    &:hover {
      background: $accent-soft;
    }
  }
  .quick-icon {
    position: relative;
    width: 40px;
    height: 40px;
    overflow: hidden;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .quick-label {
    font-size: 12px;
    color: $text-primary;
    font-weight: 400;
    text-align: center;
    line-height: 1.3;
  }

  // ④ 核心业务模块：每排 4 个，最多 12
  .module-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 26px;
    @media (max-width: 1100px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 700px) {
      grid-template-columns: 1fr;
    }
  }
  .module-empty {
    margin-bottom: 26px;
    padding: 28px 16px;
    text-align: center;
    font-size: 13px;
    color: $text-muted;
    background: #fafbfc;
    border: 1px dashed $border-color;
    border-radius: 12px;
  }
  .module-card {
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 16px;
    padding: 18px;
    cursor: pointer;
    transition:
      background-color 0.18s ease,
      border-color 0.18s ease;
    box-shadow: none;
    &:hover {
      border-color: rgba(0, 137, 255, 0.35);
      background: #fafcfe;
      box-shadow: none;
    }
  }
  .module-top {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }
  .module-icon {
    position: relative;
    width: 42px;
    height: 42px;
    overflow: hidden;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .module-name {
    font-size: 15px;
    font-weight: 600;
  }
  .module-desc {
    font-size: 12px;
    color: $text-muted;
    margin-top: 2px;
  }
  .module-info {
    font-size: 13px;
    color: $text-secondary;
    line-height: 1.5;
    min-height: 40px;
  }
  .module-date {
    font-size: 12px;
    color: $text-muted;
    text-align: right;
    margin-top: 10px;
  }

  // ⑤ 图表
  .charts-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;
    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }
  }
  .chart-card {
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 16px;
    padding: 18px 20px;
    box-shadow: none;
  }
  .chart-title {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 16px;
  }
  .chart-container {
    position: relative;
    height: 240px;
  }

  // 通用
  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .section-title {
    position: relative;
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
    padding-left: 10px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 3px;
      height: 14px;
      margin-top: -7px;
      border-radius: 2px;
      background: $accent;
    }
  }
  .view-all {
    font-size: 13px;
    color: $accent;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
